/**
 * TopBar — the shell's fixed top band.
 *
 * Visual contract (aligned with the prototype):
 *
 *   - Fixed to the viewport top (`fixed top-0 left-0 right-0 z-30`).
 *   - Height comes from the `--spacing-top-bar` token — consumed as
 *     `h-top-bar` — so the main content area can leave matching top
 *     padding (`pt-top-bar`) without arbitrary-bracket values.
 *   - Surface colour is `bg-top-bar` (`--color-top-bar`) with no bottom
 *     border, matching the prototype's edge-to-edge top band.
 *   - Centre column pinned to `max-w-md` so the mobile-shell posture is
 *     honoured on wider viewports (the brief calls out "mobile shell on
 *     desktop is mobile shell centred").
 *   - Brand mark (left edge) is the shell's `<h1>`. Per D-049 the mark
 *     renders the customer-owned `ytk-logo.png` asset (the full
 *     "YTK Työelämä" wordmark, lifted from the prototype's asset
 *     library — customer-owned imagery is in scope under the narrowed
 *     asset guard). The image's `alt` carries the accessible name
 *     "YTK Työelämä" (`common.brandFullName`); the visible wordmark in
 *     the asset matches that label so visual content and accessible
 *     name align. Outside `/login`, the image is wrapped in a home link
 *     so the brand mark returns to `/`; on `/login` it stays
 *     non-interactive because `/` is protected and would redirect
 *     straight back to login. Height is locked via `h-10` to match the
 *     prototype's TopBar treatment; width is auto so the asset's
 *     aspect ratio is preserved.
 *   - No menu trigger. Item-1 menu-position re-baseline (D-130, FR-001 +
 *     FR-056): the proto's TopBar is logo-only ("Valikko-nappi siirretty
 *     BottomNav-footeriin") and the menu opens from a "Valikko" entry in
 *     the bottom nav (`PrimaryNav`). The trigger and the `MenuSheet` body
 *     are wired together in `Layout`, so `TopBar` carries no menu API.
 *
 * TopBar takes no props and stays stateless: tests that render it in
 * isolation do not need to mount the Sheet substrate.
 */

import type { JSX } from 'react';
import { Link, useLocation } from 'react-router';

import ytkLogo from '../assets/ytk-logo.png';
import { useTranslate } from '../i18n/useTranslate.js';

// Bottom hairline removed — proto-parity for the top edge: the
// prototype's TopBar has no bottom border, so the surface reads as
// "page extends edge-to-edge under the brand band" rather than
// "chrome → border → page". The fixed positioning + `bg-top-bar`
// surface still keeps the bar visually distinct against scrolling
// content underneath.
const headerClasses = 'fixed top-0 left-0 right-0 z-30 bg-top-bar h-top-bar';
// Inner container is full-width — the brand mark sits at the actual
// viewport edge (with the `px-4` breathing room the prototype uses),
// not at the inner edge of a `max-w-md` mobile shell. The page body
// below stays `max-w-md mx-auto` per spec; only the chrome bands (top
// bar) span full width to reinforce "this is the app's frame, not the
// page". Proto-parity for the TopBar's edge alignment.
const innerClasses = 'flex h-full items-center justify-between px-4';
const titleClasses = 'm-0 inline-flex items-center text-lg font-semibold text-foreground';
// The brand mark is wrapped in a `<Link>` outside `/login` so clicking
// it routes home — proto-parity for signed-in PWA chrome. On `/login`
// the mark stays non-interactive because `/` is protected and would
// redirect back to the same screen. The link takes a `transition` +
// `active:scale-95` treatment so press feedback matches the rest of
// the shell controls; the focus-visible ring is the same one
// `PrimaryNav` and the Sheet controls share.
const brandLinkClasses =
  'inline-flex items-center rounded-md no-underline transition active:scale-95 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const brandLogoClasses = 'h-10 w-auto';

export function TopBar(): JSX.Element {
  const t = useTranslate();
  const location = useLocation();
  const isLoginRoute = location.pathname === '/login';
  const logo = <img src={ytkLogo} alt={t('common.brandFullName')} className={brandLogoClasses} />;

  return (
    <header className={headerClasses}>
      <div className={innerClasses}>
        <h1 className={titleClasses}>
          {isLoginRoute ? (
            logo
          ) : (
            <Link to="/" className={brandLinkClasses}>
              {logo}
            </Link>
          )}
        </h1>
      </div>
    </header>
  );
}
